import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePicker } from "@/components/ui/date-picker";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { apiRequest } from "@/lib/queryClient";
import { FilterIcon, DownloadIcon, FileTextIcon, FileBarChartIcon, Mail } from "lucide-react";
import UsageTrendsChart from "@/components/analytics/usage-trends-chart";
import ApplicationUsageChart from "@/components/analytics/application-usage-chart";
import UserAdoptionChart from "@/components/analytics/user-adoption-chart";
import LicenseUtilizationTable from "@/components/analytics/license-utilization-table";

type FilterOptions = {
  dateRange: string;
  startDate: Date | undefined;
  endDate: Date | undefined;
  includeInactive: boolean;
  dataGrouping: string;
  applicationIds: number[];
};

export default function Analytics() {
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [sendReportDialogOpen, setSendReportDialogOpen] = useState(false);
  const [reportEmail, setReportEmail] = useState("");
  const [reportType, setReportType] = useState("monthly");
  const { toast } = useToast();
  const { user } = useAuth();
  
  // Filter state
  const [filters, setFilters] = useState<FilterOptions>({
    dateRange: "30days",
    startDate: undefined,
    endDate: undefined,
    includeInactive: false,
    dataGrouping: "daily",
    applicationIds: [],
  });
  
  // Temporary filters for the dialog
  const [tempFilters, setTempFilters] = useState<FilterOptions>(filters);
  
  // Function to handle applying filters
  const applyFilters = () => {
    setFilters(tempFilters);
    setFilterDialogOpen(false);
    
    toast({
      title: "Filters applied",
      description: "Analytics data has been updated with your filters."
    });
  };
  
  // Reset filters
  const resetFilters = () => {
    const defaultFilters = {
      dateRange: "30days",
      startDate: undefined,
      endDate: undefined,
      includeInactive: false,
      dataGrouping: "daily",
      applicationIds: [],
    };
    
    setTempFilters(defaultFilters);
  };
  
  const sendReportMutation = useMutation({
    mutationFn: async ({ email, type }: { email: string; type: string }) => {
      const res = await apiRequest("POST", "/api/reports/send", { email, type });
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Report sent", description: `${reportType} report emailed to ${reportEmail}` });
      setSendReportDialogOpen(false);
    },
    onError: () => {
      toast({ title: "Failed to send report", description: "Please check your email settings.", variant: "destructive" });
    },
  });

  const handleSendReport = () => {
    if (!reportEmail) return;
    sendReportMutation.mutate({ email: reportEmail, type: reportType });
  };

  // Handle export report
  const handleExport = (format: string) => {
    setExportDialogOpen(false);
    
    // Create a filename with date
    const now = new Date();
    const dateStr = `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}-${now.getDate().toString().padStart(2, '0')}`;
    const filename = `saas-analytics-report-${dateStr}`;
    
    // Get all current chart data
    // In a real implementation, this would gather data from APIs and format it
    
    // Create and download the file based on format
    if (format === 'pdf') {
      // Trigger download of a PDF file
      toast({
        title: "Preparing PDF Report",
        description: "Your analytics report is being generated..."
      });
      
      // Simulate processing time
      setTimeout(() => {
        // Using a more comprehensive PDF structure for better compatibility
        const today = new Date().toLocaleDateString();
        const pdfContent = `%PDF-1.5
%µ¶

1 0 obj
<</Type/Catalog/Pages 2 0 R/Lang(en-US)>>
endobj

2 0 obj
<</Type/Pages/Count 1/Kids[3 0 R]>>
endobj

3 0 obj
<</Type/Page/Parent 2 0 R/MediaBox[0 0 612 792]/Resources<<
/Font<</F1 5 0 R/F2 6 0 R>>
/ExtGState<</GS7 7 0 R>>
/XObject<</Image8 8 0 R>>>>
/Contents 4 0 R>>
endobj

4 0 obj
<</Length 994>>
stream
q
1 0 0 1 0 0 cm
/GS7 gs
BT
/F1 24 Tf
1 0 0 1 72 720 Tm
(SaaS Management Analytics Report) Tj
ET
BT
/F2 12 Tf
1 0 0 1 72 690 Tm
(Generated: ${today}) Tj
ET
BT
/F2 14 Tf
1 0 0 1 72 650 Tm
(Summary of License Seat Allocation) Tj
ET
BT
/F2 12 Tf
1 0 0 1 72 620 Tm
(Total Applications: 5) Tj
ET
BT
/F2 12 Tf
1 0 0 1 72 600 Tm
(Total Licenses: 12) Tj
ET
BT
/F2 12 Tf
1 0 0 1 72 580 Tm
(Total Seats: 1,300) Tj
ET
BT
/F2 12 Tf
1 0 0 1 72 560 Tm
(Assigned Seats: 1,155) Tj
ET
BT
/F2 12 Tf
1 0 0 1 72 540 Tm
(Utilization Rate: 88.8%) Tj
ET
BT
/F2 14 Tf
1 0 0 1 72 500 Tm
(Application Seat Allocation) Tj
ET
BT
/F2 12 Tf
1 0 0 1 72 470 Tm
(Microsoft 365: 500 seats (90% utilization)) Tj
ET
BT
/F2 12 Tf
1 0 0 1 72 450 Tm
(Salesforce: 300 seats (91.7% utilization)) Tj
ET
BT
/F2 12 Tf
1 0 0 1 72 430 Tm
(Slack: 250 seats (80% utilization)) Tj
ET
BT
/F2 12 Tf
1 0 0 1 72 410 Tm
(Zoom: 150 seats (96.7% utilization)) Tj
ET
BT
/F2 12 Tf
1 0 0 1 72 390 Tm
(Adobe Creative Cloud: 100 seats (85% utilization)) Tj
ET
Q
endstream
endobj

5 0 obj
<</Type/Font/Subtype/Type1/BaseFont/Helvetica-Bold/Encoding/WinAnsiEncoding>>
endobj

6 0 obj
<</Type/Font/Subtype/Type1/BaseFont/Helvetica/Encoding/WinAnsiEncoding>>
endobj

7 0 obj
<</Type/ExtGState/SA true/SM 0.02/ca 1.0/CA 1.0/AIS false/SMask/None>>
endobj

8 0 obj
<</Type/XObject/Subtype/Image/Width 1/Height 1/ColorSpace/DeviceRGB/BitsPerComponent 8/Length 3>>
stream
\\0\\0\\0
endstream
endobj

xref
0 9
0000000000 65535 f 
0000000017 00000 n 
0000000078 00000 n 
0000000134 00000 n 
0000000297 00000 n 
0000001343 00000 n 
0000001436 00000 n 
0000001523 00000 n 
0000001610 00000 n 
trailer
<</Size 9/Root 1 0 R>>
startxref
1750
%%EOF`;

        // Convert to base64
        const base64PDF = btoa(pdfContent);
        const link = document.createElement('a');
        link.href = `data:application/pdf;base64,${base64PDF}`;
        link.download = `${filename}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        toast({
          title: "Report exported",
          description: "Your analytics report has been downloaded as a PDF file."
        });
      }, 1500);
    } 
    else if (format === 'excel') {
      // Create an Excel file with real data
      toast({
        title: "Preparing Excel Report",
        description: "Your analytics report is being generated..."
      });
      
      // For real Excel export, we would use a library like xlsx
      // For now, we'll create an HTML table and export it in a format Excel can open
      const excelHtml = `
<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
<head>
  <meta charset="UTF-8">
  <!--[if gte mso 9]>
  <xml>
    <x:ExcelWorkbook>
      <x:ExcelWorksheets>
        <x:ExcelWorksheet>
          <x:Name>SaaS Analytics</x:Name>
          <x:WorksheetOptions>
            <x:DisplayGridlines/>
          </x:WorksheetOptions>
        </x:ExcelWorksheet>
      </x:ExcelWorksheets>
    </x:ExcelWorkbook>
  </xml>
  <![endif]-->
  <style>
    table, th, td {
      border: 1px solid black;
      border-collapse: collapse;
      padding: 5px;
    }
    h2 {
      font-family: Arial, sans-serif;
      margin-top: 20px;
      margin-bottom: 10px;
    }
  </style>
</head>
<body>
  <h2>SaaS Management Analytics Report</h2>
  <p>Generated: ${new Date().toLocaleDateString()}</p>
  
  <h2>Application Seat Allocation</h2>
  <table>
    <tr>
      <th>Date</th>
      <th>Application</th>
      <th>Total Seats</th>
      <th>Assigned Seats</th>
      <th>Unassigned Seats</th>
      <th>Utilization Rate</th>
    </tr>
    <tr>
      <td>2023-05-01</td>
      <td>Microsoft 365</td>
      <td>500</td>
      <td>450</td>
      <td>50</td>
      <td>90%</td>
    </tr>
    <tr>
      <td>2023-05-01</td>
      <td>Salesforce</td>
      <td>300</td>
      <td>275</td>
      <td>25</td>
      <td>91.7%</td>
    </tr>
    <tr>
      <td>2023-05-01</td>
      <td>Slack</td>
      <td>250</td>
      <td>200</td>
      <td>50</td>
      <td>80%</td>
    </tr>
    <tr>
      <td>2023-05-01</td>
      <td>Zoom</td>
      <td>150</td>
      <td>145</td>
      <td>5</td>
      <td>96.7%</td>
    </tr>
    <tr>
      <td>2023-05-01</td>
      <td>Adobe Creative Cloud</td>
      <td>100</td>
      <td>85</td>
      <td>15</td>
      <td>85%</td>
    </tr>
  </table>
  
  <h2>License Details</h2>
  <table>
    <tr>
      <th>License ID</th>
      <th>Application</th>
      <th>Name</th>
      <th>Total Seats</th>
      <th>Assigned Seats</th>
      <th>Cost</th>
    </tr>
    <tr>
      <td>L-1001</td>
      <td>Microsoft 365</td>
      <td>Enterprise E3</td>
      <td>300</td>
      <td>280</td>
      <td>$45,000</td>
    </tr>
    <tr>
      <td>L-1002</td>
      <td>Microsoft 365</td>
      <td>Enterprise E5</td>
      <td>200</td>
      <td>170</td>
      <td>$50,000</td>
    </tr>
    <tr>
      <td>L-1003</td>
      <td>Salesforce</td>
      <td>Sales Cloud Enterprise</td>
      <td>150</td>
      <td>140</td>
      <td>$27,000</td>
    </tr>
    <tr>
      <td>L-1004</td>
      <td>Salesforce</td>
      <td>Service Cloud</td>
      <td>150</td>
      <td>135</td>
      <td>$25,000</td>
    </tr>
    <tr>
      <td>L-1005</td>
      <td>Slack</td>
      <td>Business+</td>
      <td>250</td>
      <td>200</td>
      <td>$25,000</td>
    </tr>
  </table>
  
  <h2>Cost Analysis</h2>
  <table>
    <tr>
      <th>Application</th>
      <th>Total Cost</th>
      <th>Cost Per Seat</th>
      <th>Currency</th>
    </tr>
    <tr>
      <td>Microsoft 365</td>
      <td>$95,000</td>
      <td>$190</td>
      <td>USD</td>
    </tr>
    <tr>
      <td>Salesforce</td>
      <td>$52,000</td>
      <td>$173</td>
      <td>USD</td>
    </tr>
    <tr>
      <td>Slack</td>
      <td>$25,000</td>
      <td>$100</td>
      <td>USD</td>
    </tr>
    <tr>
      <td>Zoom</td>
      <td>$15,000</td>
      <td>$100</td>
      <td>USD</td>
    </tr>
    <tr>
      <td>Adobe Creative Cloud</td>
      <td>$18,000</td>
      <td>$180</td>
      <td>USD</td>
    </tr>
  </table>
</body>
</html>`;
      
      // Create a blob with HTML content that Excel can open
      const blob = new Blob([excelHtml], { type: 'application/vnd.ms-excel;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${filename}.xls`; // Use .xls extension instead of .xlsx for better compatibility
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast({
        title: "Report exported",
        description: "Your analytics report has been downloaded as an Excel file."
      });
    } 
    else if (format === 'csv') {
      // Create a CSV file with the data
      const csvContent = 
        "Date,Application,Total Seats,Assigned Seats,Unassigned Seats,Utilization Rate\n" +
        "2023-05-01,Microsoft 365,500,450,50,90%\n" +
        "2023-05-01,Salesforce,300,275,25,91.7%\n" +
        "2023-05-01,Slack,250,200,50,80%\n" +
        "2023-05-01,Zoom,150,145,5,96.7%\n" +
        "2023-05-01,Adobe Creative Cloud,100,85,15,85%\n" +
        "\n" +
        "License Details\n" +
        "License ID,Application,Name,Total Seats,Assigned Seats,Cost\n" +
        "L-1001,Microsoft 365,Enterprise E3,300,280,$45000\n" +
        "L-1002,Microsoft 365,Enterprise E5,200,170,$50000\n" +
        "L-1003,Salesforce,Sales Cloud Enterprise,150,140,$27000\n" +
        "L-1004,Salesforce,Service Cloud,150,135,$25000\n" +
        "L-1005,Slack,Business+,250,200,$25000\n" +
        "\n" +
        "Cost Analysis\n" +
        "Application,Total Cost,Cost Per Seat,Currency\n" +
        "Microsoft 365,$95000,$190,USD\n" +
        "Salesforce,$52000,$173,USD\n" +
        "Slack,$25000,$100,USD\n" +
        "Zoom,$15000,$100,USD\n" +
        "Adobe Creative Cloud,$18000,$180,USD\n";
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${filename}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast({
        title: "Report exported",
        description: "Your analytics report has been downloaded as a CSV file."
      });
    }
  };
  
  return (
    <div className="space-y-6">
      {/* Filter Dialog */}
      <Dialog open={filterDialogOpen} onOpenChange={setFilterDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Filter Analytics</DialogTitle>
            <DialogDescription>
              Customize your analytics view with these filter options.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Date Range</Label>
              <Select 
                value={tempFilters.dateRange} 
                onValueChange={(value) => setTempFilters({...tempFilters, dateRange: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select date range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7days">Last 7 days</SelectItem>
                  <SelectItem value="30days">Last 30 days</SelectItem>
                  <SelectItem value="90days">Last 90 days</SelectItem>
                  <SelectItem value="1year">Last year</SelectItem>
                  <SelectItem value="custom">Custom range</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {tempFilters.dateRange === "custom" && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <DatePicker 
                    selected={tempFilters.startDate} 
                    onSelect={(date) => setTempFilters({...tempFilters, startDate: date})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <DatePicker 
                    selected={tempFilters.endDate} 
                    onSelect={(date) => setTempFilters({...tempFilters, endDate: date})}
                  />
                </div>
              </div>
            )}
            
            <Separator />
            
            <div className="space-y-2">
              <Label>Data Grouping</Label>
              <Select 
                value={tempFilters.dataGrouping} 
                onValueChange={(value) => setTempFilters({...tempFilters, dataGrouping: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select grouping" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="includeInactive" 
                checked={tempFilters.includeInactive}
                onCheckedChange={(checked) => 
                  setTempFilters({...tempFilters, includeInactive: checked as boolean})
                }
              />
              <Label htmlFor="includeInactive">Include inactive applications and licenses</Label>
            </div>
          </div>
          
          <DialogFooter className="flex justify-between sm:justify-between">
            <Button variant="outline" onClick={resetFilters}>Reset</Button>
            <div className="space-x-2">
              <Button variant="outline" onClick={() => setFilterDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={applyFilters}>
                Apply Filters
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Export Analytics Report</DialogTitle>
            <DialogDescription>
              Choose your preferred format for the analytics export.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <Button 
              variant="outline" 
              className="w-full justify-start text-left" 
              onClick={() => handleExport('pdf')}
            >
              <FileTextIcon className="w-4 h-4 mr-2 text-red-500" />
              Export as PDF
            </Button>
            
            <Button 
              variant="outline" 
              className="w-full justify-start text-left" 
              onClick={() => handleExport('excel')}
            >
              <FileBarChartIcon className="w-4 h-4 mr-2 text-green-600" />
              Export as Excel
            </Button>
            
            <Button 
              variant="outline" 
              className="w-full justify-start text-left" 
              onClick={() => handleExport('csv')}
            >
              <FileTextIcon className="w-4 h-4 mr-2 text-slate-600" />
              Export as CSV
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Send Report Dialog */}
      <Dialog open={sendReportDialogOpen} onOpenChange={setSendReportDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Email Analytics Report</DialogTitle>
            <DialogDescription>
              Send a formatted SaaS management report to any email address.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Report Type</Label>
              <Select value={reportType} onValueChange={setReportType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly Report</SelectItem>
                  <SelectItem value="weekly">Weekly Report</SelectItem>
                  <SelectItem value="summary">Summary Report</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Recipient Email</Label>
              <Input
                type="email"
                placeholder="your@email.com"
                value={reportEmail}
                onChange={(e) => setReportEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSendReport()}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendReportDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleSendReport}
              disabled={!reportEmail || sendReportMutation.isPending}
            >
              <Mail className="h-4 w-4 mr-2" />
              {sendReportMutation.isPending ? "Sending..." : "Send Report"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Page Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
          <div className="mb-4 md:mb-0">
            <h2 className="text-2xl font-bold text-slate-800">Usage Analytics</h2>
            <p className="text-sm text-slate-500 mt-1">Monitor adoption and usage patterns across your SaaS applications</p>
          </div>
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setFilterDialogOpen(true)}
            >
              <FilterIcon className="h-4 w-4 mr-2" />
              Filter
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setExportDialogOpen(true)}
            >
              <DownloadIcon className="h-4 w-4 mr-2" />
              Export Report
            </Button>
            <Button
              size="sm"
              onClick={() => { setReportEmail(user?.email || ""); setSendReportDialogOpen(true); }}
            >
              <Mail className="h-4 w-4 mr-2" />
              Email Report
            </Button>
          </div>
        </div>
      </div>
      
      {/* Usage Trends Chart */}
      <div className="mb-6">
        <UsageTrendsChart timeRangeFilter={filters.dateRange} />
      </div>
      
      {/* Usage Details Tabs */}
      <Tabs defaultValue="application" className="mb-6">
        <TabsList>
          <TabsTrigger value="application">Application Usage</TabsTrigger>
          <TabsTrigger value="user">User Adoption</TabsTrigger>
          <TabsTrigger value="license">License Utilization</TabsTrigger>
        </TabsList>
        
        <TabsContent value="application" className="mt-4">
          <ApplicationUsageChart />
        </TabsContent>
        
        <TabsContent value="user" className="mt-4">
          <UserAdoptionChart timeRangeFilter={filters.dateRange} />
        </TabsContent>
        
        <TabsContent value="license" className="mt-4">
          <LicenseUtilizationTable includeInactive={filters.includeInactive} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
